/**
 * S4: connect your code. Step two of three.
 *
 * The highest risk screen in the product: the most likely place a first run
 * dies, and the place where the failure is least the person's fault. Every
 * decision here follows from that.
 *
 * SCOPE REASSURANCE COMES BEFORE THE PERMISSION IS ASKED FOR. "It only sees
 * the repositories you pick" is on the screen above the button, not in a
 * tooltip and not on GitHub's consent page, because the moment a person needs
 * it is the moment before they click.
 *
 * A NON EMPTY REPOSITORY DOES NOT BLOCK. Selecting one swaps the consequence
 * panel for an explanation and a different primary action. The brief calls
 * this the most likely first run failure, and the difference between a product
 * people finish and one they abandon is whether the screen says "no" or says
 * "here is the other way".
 *
 * WHEN GITHUB IS DOWN WE SAY SO, PLAINLY, AND SAY IT IS NOT THEIR FAULT. That
 * one sentence is the kindest thing this screen does, and it costs nothing.
 *
 * Already connected repositories stay in the list, dimmed, labelled, with a
 * link to the project that has them. Hiding them would make a person think
 * GitHub is broken, and then they would go and check GitHub.
 *
 * States, every one deep linkable, per screen spec 1.3a:
 *   /new/connect                        app not installed (sub-state A)
 *   /new/connect?state=installing       returning from GitHub, narrated
 *   /new/connect?state=picking          the repository list (sub-state B)
 *   /new/connect?state=create           we will make one for you (sub-state C)
 *   /new/connect?state=no-repos         access granted, nothing shared
 *   /new/connect?state=github-down      GitHub is not answering
 * Add ?adv=1 to any of them for the branch, template and file list. It shows
 * on the create state and on any picked repository, empty or not: the branch
 * picker exists for the person whose repository already has a branch.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { GitHubMark } from '../../components/brand/GitHubMark';
import { Button } from '../../components/primitives/Button';
import { Select } from '../../components/primitives/Select';
import { AsyncNarration } from '../../components/patterns/AsyncNarration';
import { StepDots } from '../../components/patterns/StepDots';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { BackLink } from './BackLink';

import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAccount, useProjects } from '../../lib/auth/use-app-data';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { useTicker } from '../../lib/hooks/use-ticker';
import { bytes, relativeTime } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import { ApiError } from '../../lib/http';
import { clearDraft, readDraft } from '../../lib/onboarding/draft';
import { captureEvent } from '../../lib/analytics/posthog';
import { useInactivityTimer } from '../../lib/analytics/use-frustration-detector';
import {
  connectProject,
  getInstallation,
  getInstallConnectUrl,
  listInstallations,
  type InstallationContext,
} from '../../lib/real-api';
import {
  ONBOARDING_STEPS,
  REPO_SEARCH_THRESHOLD,
  STARTER_FILES,
  STARTER_TEMPLATE,
  repoCandidates,
} from '../../lib/mock/onboarding';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import type { RepoCandidate } from '../../lib/types';

import '../../styles/screens/onboarding.css';
// The ADVANCED chip is one treatment in one place. This screen borrows the
// disclosure's rather than drawing a fifth copy of the same 20px box.
import '../../styles/components/disclosure.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

const STATES = ['checking', 'not-installed', 'installing', 'picking', 'create', 'no-repos', 'github-down'] as const;
type State = (typeof STATES)[number];

const INSTALL_TIMELINE = 'github-install';

/** ApiError.code → this screen's own words, for the errors POST /projects/connect can return. */
function connectErrorMessage(code: string): string {
  switch (code) {
    case 'SUBDOMAIN_TAKEN':
      return t('That web address was just taken. Go back and pick another name.');
    case 'REPO_ALREADY_CONNECTED':
      return t('This repository is already connected to a different project.');
    case 'PROJECT_QUOTA_EXCEEDED':
      // Deliberately says no number. The cap is a server var
      // (PROJECT_QUOTA_PER_ACCOUNT) that moved from 1 to 3 the day students
      // were let in, and a figure baked in here would go stale the next time
      // it moves without anything failing to say so.
      return t('This account is already using all the active projects it may have.');
    case 'FORBIDDEN':
      return t('That GitHub connection is no longer linked to your account. Reconnect and try again.');
    default:
      return t('Something went wrong connecting your project. Try again.');
  }
}

/* ------------------------------------------------------------- repo row */

interface RepoRowProps {
  repo: RepoCandidate;
  connectedTo: string | null;
  selected: boolean;
  onPick: () => void;
  now: number;
}

/**
 * One row of the picker.
 *
 * A radio input, not a listbox and not a select. The native control brings the
 * arrow key map, the grouping and the screen reader announcement with it, and
 * a repository list is exactly the pick one of these shape radios are for.
 */
function RepoRow({ repo, connectedTo, selected, onPick, now }: RepoRowProps) {
  const pushed = relativeTime(repo.pushedAt, now);
  const taken = connectedTo !== null;

  return (
    <li className="repo" data-selected={selected ? 'yes' : 'no'} data-taken={taken ? 'yes' : 'no'}>
      <label className="repo__label">
        <input
          className="repo__input"
          type="radio"
          name="repository"
          checked={selected}
          disabled={taken}
          onChange={onPick}
        />
        <span className="repo__name mono">{repo.name}</span>
        <span className="repo__owner mono">{repo.owner}</span>
        <span className="repo__when">{pushed ? t(pushed.key, { n: pushed.n }) : null}</span>
      </label>

      <span className="repo__state">
        {taken ? (
          <Link className="repo__link" to={`/p/${connectedTo}`}>
            {t('Already connected')}
          </Link>
        ) : repo.isEmpty ? (
          <span className="repo__empty">{t('Empty')}</span>
        ) : (
          <span className="repo__has-files">{t('Has files')}</span>
        )}
      </span>
    </li>
  );
}

/* ---------------------------------------------------------------- screen */

export default function ConnectCode() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [advanced] = useAdvanced();
  const { state, setState } = useScreenState<State>(STATES, USE_MOCK ? 'not-installed' : 'checking');

  const demoAccount = useDemoStore((s) => s.account);
  const demoProjects = useDemoStore((s) => s.projects);
  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const now = useTicker();

  // Real mode only, the caller's GitHub App installation (mount effect
  // below resolves this from GET /installations or the ?installation_id=
  // the App's install callback redirects back with) and its repo list, kept
  // separate from the mock's account/repoCandidates so the two flows never
  // cross-read each other's data.
  const account = useAccount();
  const realProjects = useProjects();
  const [installationId, setInstallationId] = useState<string | null>(null);
  const [installContext, setInstallContext] = useState<InstallationContext | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [wantsNew, setWantsNew] = useState(false);
  const [branch, setBranch] = useState('main');

  const app = params.get('app') ?? 'herb-and-ash';
  const draft = useMemo(() => readDraft(), []);
  const repos = useMemo(
    () => (USE_MOCK ? repoCandidates(demoAccount.githubHandle) : (installContext?.repos ?? [])),
    [demoAccount.githubHandle, installContext],
  );

  /** Derived, never stored: which project already has each repository. */
  const connections = useMemo(() => {
    const map = new Map<string, string>();
    const source = USE_MOCK ? demoProjects : realProjects;
    for (const project of source) {
      if (project.repo) map.set(`${project.repo.owner}/${project.repo.name}`, project.slug);
    }
    return map;
  }, [demoProjects, realProjects]);

  const selected = repos.find((repo) => repo.id === selectedId) ?? null;

  const install = timelines.find((line) => line.id === INSTALL_TIMELINE);
  const installState = install ? stateOf(install, now) : null;

  // The install narration hands over the moment it lands. Leaving a finished
  // checklist on screen with a button under it would make a person wonder
  // which of the two they are waiting for.
  useEffect(() => {
    if (!USE_MOCK) return;
    if (state !== 'installing') return;
    if (installState?.status === 'succeeded') setState('picking');
  }, [state, installState?.status, setState]);

  /**
   * Real mode's whole "am I connected to GitHub" resolution: called on
   * mount, and again by "Try again" on the github-down state. An
   * `?installation_id=` in the URL means we just landed back from the App's
   * install callback (routes/installations.ts's redirect) and should trust
   * it over whatever GET /installations would otherwise return first.
   */
  const loadInstallation = useCallback(async () => {
    const fromCallback = params.get('installation_id');
    try {
      const id = fromCallback ?? (await listInstallations())[0]?.id ?? null;
      if (!id) {
        setState('not-installed');
        return;
      }
      setInstallationId(id);
      const ctx = await getInstallation(id);
      setInstallContext(ctx);
      setState(ctx.repos.length === 0 ? 'no-repos' : 'picking');
    } catch {
      setState('github-down');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- params is a fresh object every render; only its installation_id value matters
  }, [params.get('installation_id'), setState]);

  useEffect(() => {
    if (USE_MOCK) return;
    void loadInstallation();
    // Deliberately mount-only (plus a fresh installation_id), loadInstallation
    // itself calls setState, which would otherwise re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('installation_id')]);

  useInactivityTimer({
    screenName: 'ConnectCode',
    thresholdMs: 45000,
    currentStep: 1,
    expectedAction: 'connect_repository',
  });

  useEffect(() => {
    captureEvent('connect_code_state_viewed', {
      state,
      app,
      screen_path: window.location.pathname,
    });
  }, [state, app]);

  /* `newTab` is for the quiet "manage which repositories" route: a person who
     takes it is mid-connect and should come back to a screen that still has
     their picked repository on it. The tab is opened synchronously on the
     click, before the URL is fetched, or the popup blocker eats it. */
  function beginInstall(newTab = false) {
    captureEvent('connect_code_begin_install', { app, mode: USE_MOCK ? 'mock' : 'real', new_tab: newTab });
    if (!USE_MOCK) {
      const tab = newTab ? window.open('', '_blank', 'noopener,noreferrer') : null;
      getInstallConnectUrl()
        .then((url) => {
          if (newTab) {
            if (tab) tab.location.href = url;
            else window.open(url, '_blank', 'noopener,noreferrer');
            return;
          }
          window.location.assign(url);
        })
        .catch(() => {
          tab?.close();
          captureEvent('connect_code_install_url_failed');
          setState('github-down');
        });
      return;
    }
    startTimeline({
      id: INSTALL_TIMELINE,
      kind: 'github-install',
      scriptId: 'github-install',
      subjectId: app,
      startedAt: Date.now(),
      outcome: 'success',
    });
    setState('installing');
  }

  function handleConnect() {
    if (!selected || !installationId) return;
    setConnecting(true);
    setConnectError(null);
    const name = draft?.name ?? app;
    const repoFullName = `${selected.owner}/${selected.name}`;

    captureEvent('connect_code_submitted', {
      app,
      name,
      repoFullName,
      branch,
      installationId,
    });

    connectProject({
      name,
      repoFullName,
      githubInstallationId: installationId,
      requestedSubdomain: draft?.slug ?? app,
    })
      .then((result) => {
        clearDraft();
        captureEvent('connect_code_success', {
          projectId: result.projectId,
          slug: result.slug,
          runId: result.runId,
        });
        if (result.runId) {
          navigate(
            `/new/building?project=${result.projectId}&run=${result.runId}&slug=${result.slug}`,
            // FirstBuild needs these to retry the connect call if setup fails.
            { state: { name, repoFullName, githubInstallationId: installationId } },
          );
        } else {
          navigate(`/p/${result.slug}`);
        }
      })
      .catch((err: unknown) => {
        const errorCode = err instanceof ApiError ? err.code : 'unknown';
        captureEvent('connect_code_failed', {
          error_code: errorCode,
          repoFullName,
        });
        setConnecting(false);
        setConnectError(err instanceof ApiError ? connectErrorMessage(err.code) : connectErrorMessage(''));
      });
  }

  /* ------------------------------------------------------------- panels */

  const consequence = () => {
    if (!selected) return null;

    if (!selected.isEmpty) {
      // Mock apparatus copy is untouched, its onboarding fixtures still
      // model the old "empty repository required" behavior end to end
      // (STARTER_FILES, the create-a-new-one detour). Real mode reflects
      // what bundleInitCommit (services/github.ts) actually does today: one
      // additive commit on top of whatever the repo already has, never a
      // force-push, so a repository with files in it is no longer a wall.
      if (USE_MOCK) {
        return (
          <Notice tone="attention">
            <p>
              {t(
                '{repo} already has files in it. SV Cloud starts from an empty repository so it can add the starter files without touching your work. Pick an empty one, or let us make a new repository for you.',
                { repo: selected.name },
              )}
            </p>
          </Notice>
        );
      }
      return (
        <Notice>
          <p>
            {t(
              "{repo} already has files in it. SV Cloud will add its starter files in one new commit on top of what's there, nothing existing is touched or force-pushed.",
              { repo: selected.name },
            )}
          </p>
        </Notice>
      );
    }

    return (
      <Notice>
        <p>{t("We'll add starter files to {repo} and deploy it.", { repo: selected.name })}</p>
      </Notice>
    );
  };

  /**
   * The Advanced panel, gated on the mode and nothing else.
   *
   * It used to be gated on `advanced && selected`, and then the picking call
   * site gated it a second time on `selected?.isEmpty`. The person that
   * excluded is exactly the person who wants it: somebody in Advanced who has
   * picked a repository that already has files in it, and who now needs to say
   * which branch. The mode is the only condition here; whether a repository has
   * been chosen is the call site's question, and the help line below answers
   * for both cases rather than assuming one.
   */
  const advancedPanel = advanced ? (
    <section className="onb__advanced">
      <span className="disc__chip">{t('ADVANCED')}</span>

      <Select
        label={t('Branch')}
        value={branch}
        onChange={(event) => setBranch(event.target.value)}
        options={[
          { value: 'main', label: 'main' },
          { value: 'master', label: 'master' },
        ]}
        help={
          selected && !selected.isEmpty
            ? undefined
            : t('This repository has no branches yet. We will create this one.')
        }
      />

      <Select
        label={t('Starter template')}
        options={[{ value: STARTER_TEMPLATE, label: STARTER_TEMPLATE }]}
        help={t('One template exists today. More will not change what Simple mode does.')}
      />

      <div className="files">
        <p className="files__head">{t("Files we'll commit")}</p>
        <ul className="files__list">
          {STARTER_FILES.map((file) => (
            <li className="files__row" key={file.path}>
              <span className="files__path mono">{file.path}</span>
              <span className="files__size mono">{bytes(file.bytes)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  ) : null;

  /* -------------------------------------------------------------- render */

  return (
    <div className="onb">
      <StepDots
        steps={ONBOARDING_STEPS.map((step) => t(step))}
        current={1}
        label={t('Setting up your project')}
      />

      <div className="onb__col onb__col--wide">
        <BackLink to={account.svgitEnabled ? `/new/code?app=${app}` : '/new'} />
        {state === 'checking' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Connect your code')}</h1>
              <p className="onb__say">
                {t('Checking your GitHub connection...')}
              </p>
            </header>

            <div className="onb__checking" role="status" aria-live="polite">
              <span className="onb__spinner" aria-hidden="true" />
              <span className="onb__checking-say mono">{t('Looking for connected repositories...')}</span>
            </div>
          </>
        ) : null}

        {state === 'not-installed' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Connect your code')}</h1>
              <p className="onb__say">
                {t(
                  'SV Cloud needs access to one GitHub repository. It only sees the repositories you pick.',
                )}
              </p>
            </header>

            <div className="onb__actions onb__actions--start">
              <Button variant="solid" onClick={() => beginInstall()}>
                <span className="onb__gh">
                  <GitHubMark />
                  {t('Continue with GitHub')}
                </span>
              </Button>
            </div>

            {/* The quiet second route. It expands in place rather than routing,
                because a person who does not have a repository has not made a
                mistake and should not be sent anywhere. */}
            {wantsNew ? (
              <div className="onb__nested">
                <h2 className="onb__sub">{t("We'll make one for you")}</h2>
                <p className="onb__say">
                  {t(
                    "We'll create a new repository called {repo} on your GitHub account and put the starter files in it.",
                    { repo: app },
                  )}
                </p>
                <div className="onb__actions onb__actions--start">
                  <Button
                    variant="outline"
                    onClick={
                      USE_MOCK
                        ? () => navigate('/new/building')
                        : () => window.open('https://github.com/new', '_blank', 'noopener')
                    }
                  >
                    {t('Create it')}
                  </Button>
                </div>
              </div>
            ) : (
              <button type="button" className="onb__quiet" onClick={() => setWantsNew(true)}>
                {t("I don't have a repository yet")}
              </button>
            )}
          </>
        ) : null}

        {state === 'installing' && install ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Connecting to GitHub')}</h1>
            </header>
            <AsyncNarration timeline={install} />
          </>
        ) : null}

        {state === 'github-down' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Pick a repository')}</h1>
            </header>
            <Notice
              tone="attention"
              actions={
                <>
                  <Button
                    variant="solid"
                    onClick={
                      USE_MOCK
                        ? () => beginInstall()
                        : () => {
                            setState('checking');
                            void loadInstallation();
                          }
                    }
                  >
                    {t('Try again')}
                  </Button>
                  <span className="onb__quiet-static">{t('GitHub status')}</span>
                </>
              }
            >
              <p>{t("GitHub isn't responding right now. This is on their side, not yours.")}</p>
              <p>{t('Nothing has been set up yet, so nothing needs undoing.')}</p>
            </Notice>
          </>
        ) : null}

        {state === 'no-repos' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Pick a repository')}</h1>
            </header>
            <EmptyState
              mark="box"
              sentence={t("SV Cloud can't see any repositories yet.")}
              context={t('You can choose which ones we see at any time, and change it later.')}
              action={
                <Button
                  variant="solid"
                  onClick={USE_MOCK ? () => setState('picking') : () => beginInstall()}
                >
                  {t('Choose repositories on GitHub')}
                </Button>
              }
            />
            <button
              type="button"
              className="onb__quiet onb__quiet--center"
              onClick={() => setState('create')}
            >
              {t("I don't have a repository yet")}
            </button>
          </>
        ) : null}

        {state === 'create' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t("We'll make one for you")}</h1>
              <p className="onb__say">
                {t(
                  "We'll create a new repository called {repo} on your GitHub account and put the starter files in it.",
                  { repo: app },
                )}
              </p>
            </header>

            {/* advancedPanel is already null unless Advanced is on, so the
                second check here was a gate on a gate. Worse, it read as if
                this call site had a rule of its own. */}
            {advancedPanel}

            <div className="onb__actions onb__actions--start">
              <Button
                variant="solid"
                onClick={
                  USE_MOCK
                    ? () => navigate('/new/building')
                    : () => window.open('https://github.com/new', '_blank', 'noopener')
                }
              >
                {t('Create it')}
              </Button>
              <button
                type="button"
                className="onb__quiet"
                onClick={USE_MOCK ? () => setState('picking') : () => void loadInstallation()}
              >
                {t('Pick a repository instead')}
              </button>
            </div>
          </>
        ) : null}

        {state === 'picking' ? (
          <>
            <header className="onb__head">
              <h1 className="onb__title">{t('Pick a repository')}</h1>
              <p className="onb__say">
                {t('Connecting as {handle}', {
                  handle: `@${USE_MOCK ? demoAccount.githubHandle : (installContext?.accountLogin ?? account.githubHandle)}`,
                })}{' '}
                <button type="button" className="onb__inline" onClick={() => setState('not-installed')}>
                  {t('Use a different account')}
                </button>
              </p>
            </header>

            {/* The search field is a rule, not a decoration: it appears when a
                list stops being scannable, and six rows is scannable. */}
            {repos.length > REPO_SEARCH_THRESHOLD ? (
              <p className="onb__say">{t('Search your repositories')}</p>
            ) : null}

            <ul className="repos" role="radiogroup" aria-label={t('Your repositories')}>
              {repos.map((repo) => (
                <RepoRow
                  key={repo.id}
                  repo={repo}
                  now={now}
                  connectedTo={connections.get(`${repo.owner}/${repo.name}`) ?? null}
                  selected={selectedId === repo.id}
                  onPick={() => setSelectedId(repo.id)}
                />
              ))}
            </ul>

            {consequence()}
            {/* Any picked repository, not only an empty one. A person in
                Advanced who picked a repository with files in it is the one
                who most needs to say which branch, and they used to be the one
                person the panel refused to appear for. */}
            {selected ? advancedPanel : null}

            {!USE_MOCK && connectError ? (
              <Notice tone="attention">
                <p>{connectError}</p>
              </Notice>
            ) : null}

            <div className="onb__actions onb__actions--start">
              {USE_MOCK && selected && !selected.isEmpty ? (
                <Button variant="solid" onClick={() => setState('create')}>
                  {t('Make a new repository instead')}
                </Button>
              ) : (
                <Button
                  variant="solid"
                  disabled={!selected || connecting}
                  onClick={USE_MOCK ? () => navigate('/new/building') : handleConnect}
                >
                  {connecting ? t('Connecting…') : t('Connect')}
                </Button>
              )}
              <button type="button" className="onb__quiet" onClick={() => beginInstall(true)}>
                {t('Manage which repositories SV Cloud can see')}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
